/* import libraries */
import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";

/* import components */
import Button from "react-bootstrap/Button";
import Form from 'react-bootstrap/Form';
import Modal from 'react-bootstrap/Modal';
import TripCard from "./TripCard";
import Loading from "./Spinner";
import TrashIcon from "./icons/TrashIcon";

/* import services */
import ticketsApi from "../api/ticketsApi";
import { getCharacterCount } from "../utils/utils";
import { supportedFileTypes } from "../utils/constants";
import { initialBodyValue, DELETE_ACCOUNT_ARTICLE_ID, MAX_ATTACHMENTS } from "./variables/variables";

const HelpFormInfo = (props) => {
    const [loading, setLoading] = useState(false);
    const [issue, setIssue] = useState();
    const [fields, setFields] = useState([]);
    const [formBody, setFormBody] = useState(initialBodyValue);
    const [submitted, setSubmitted] = useState(false);
    const [requiredError, setRequiredError] = useState(false);
    const [validated, setValidated] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [showModalConfirm, setShowModalConfirm] = useState(false);
    const formFields = props.formData.fields;
    const trip = props.trip;
    const [attachmentMap, setAttachmentMap] = useState({})
    const navigate = useNavigate();

    const handleUpload = (event) => {
        const label = event.target.getAttribute("label")
        const fileInput = document.getElementById(label)
        if (fileInput) {
            fileInput.click()
        }
    }

    const handleRemove = (f, event, field) => {
        const fieldName = field.label
        let updatedFiles = Array.from(attachmentMap[`${fieldName}`].uploadedFiles).filter(file => {
            return file !== f;
        });

        if (!updatedFiles || updatedFiles.length === 0) {
            // REPLACE THE FILE WITH NEW FILE FOR THROWING VALIDATION
            const existingFileInput = document.getElementById(fieldName)
            const newFileInput = document.createElement('input')
            newFileInput.type = 'file';
            newFileInput.id = field.label;
            newFileInput.required = field.required;
            newFileInput.className = 'd-none';
            newFileInput.multiple = true;
            newFileInput.addEventListener('change', (event) => handleDisplayImageDetails(field, event));

            // Replace the existing input with the new one
            existingFileInput.parentNode.replaceChild(newFileInput, existingFileInput);
        }

        setAttachmentMap((prevState) => {
            return {
                ...prevState,
                [`${fieldName}`]: {
                    uploadedFiles: updatedFiles
                } 
            }
        })
    }

    const handleAttachmentMapInit = () => {
        let attachMap = {}
        for (const field of formFields) {
            if (field.inputType === "attachment") {
                attachMap[`${field.label}`] = {
                    uploadedFiles: [],
                    lengthError: false,
                    typeError: false
                }
            }
        }

        setAttachmentMap(attachMap)
    }
    
    const handleDisplayImageDetails = (field, event) => {
        // BASED ON FIELD NAME GET THE KEY FROM ATTACHMENT MAP
        const fieldName = field.label
        const keyUploadedFiles = attachmentMap[`${fieldName}`]
        const files = event.target.files
        for (const file of files) {
            let fileType = file.type;
            if (!supportedFileTypes.hasOwnProperty(fileType)) {
                setAttachmentMap((prevState) => {
                    return {
                        ...prevState,
                        [`${fieldName}`]: {
                            ...prevState[`${fieldName}`],
                            typeError: true,
                            lengthError: false
                        } 

                    }
                })
                return;
            }
        }
        if (keyUploadedFiles?.uploadedFiles?.length + files.length > MAX_ATTACHMENTS) {
            setAttachmentMap((prevState) => {
                return {
                    ...prevState,
                    [`${fieldName}`]: {
                        ...prevState[`${fieldName}`],
                        lengthError: true,
                        typeError:false
                    } 
                }
            })
            return
        } 
       
        setAttachmentMap((prevState) => {
            return {
                ...prevState,
                [`${fieldName}`]: {
                    uploadedFiles: [...prevState[`${fieldName}`]?.uploadedFiles, ...files],
                    typeError: false,
                    lengthError: false
                } 
            }
        })
    }

    const submitForm = (event) => {
        let uploadedFiles = [];
        for (const key in attachmentMap) {
            if (attachmentMap.hasOwnProperty(key)) {
                const files = attachmentMap[key].uploadedFiles;
                if (Array.isArray(files)) {
                    uploadedFiles = uploadedFiles.concat(files);
                }
            }
        }

        const form = event.currentTarget;
        if (!form.checkValidity()) {
            event.preventDefault();
            event.stopPropagation();
            setRequiredError(true);
        }
        // Show modal for delete account article 
        else if (form.checkValidity() && props.article.id === DELETE_ACCOUNT_ARTICLE_ID && !showModal && !showModalConfirm) {
            event.preventDefault();
            event.stopPropagation();
            setRequiredError(false);
            setShowModal(true);
        } else {
            setValidated(true);
            setRequiredError(false);
            const formData = new FormData();
            for (let i = 0; i < uploadedFiles.length; i++) {
                formData.append(`attachments${i}`, uploadedFiles[i]);
            }
            formData.append('articleId', formBody.articleId);
            formData.append('articleName', formBody.articleName);
            formData.append('tripId', formBody.tripId);
            formData.append('fields', JSON.stringify(fields));
            formData.append('receiptNo', formBody.receiptNo);
            createTicket(Object.fromEntries(formData));
        }
    }

    const createTicket = async (formBody) => {
        try {
            setLoading(true);
            const response = await ticketsApi.createTicket(formBody);
            if (response.status) {
                setSubmitted(true);
                setFormBody(initialBodyValue);
                setFields([]);
                setIssue(response.data.data)
            } else {
              console.error(response.error.msg);
            }
        } catch (error) {
            console.error("Error while creating ticket: " + error.message)
        } finally {
            setLoading(false);
        }
    }

    const handleFieldChange = (fieldName, event, charMaxLength) => {
        let newFields = [...fields];
        let fieldValue = event.target.value;
        if (newFields.length === 0) newFields.push({"name":fieldName, "value":fieldValue});
        let isModified = false;
        for (const field of newFields) {
            if (field.name === fieldName && ((charMaxLength > 0 && fieldValue.length <= charMaxLength) || charMaxLength === 0)) {
                field.value = fieldValue;
                isModified = true;
            }
        }
        if (!isModified) newFields = [...newFields, {"name":fieldName, "value":fieldValue}];
        setFields(newFields);
    };  

    const showFields = (field) => {
        switch(field.inputType) {
            case "textShort":
                return (
                    <>
                        <Form.Control 
                            className="input" 
                            type="text" 
                            maxLength="40"
                            required={field.required}
                            placeholder={field.placeholder}
                            onChange={(e) => handleFieldChange(field.label, e, 40)} />
                        <p style={{ color: '#404042', float: 'right', fontSize: '12px'}}>
                            {getCharacterCount(field, fields)} / 40
                        </p>
                        {requiredError ? 
                        <Form.Control.Feedback type="invalid">
                            Required field empty.
                        </Form.Control.Feedback> : <></>}
                    </>
                );
            case "textLong":
                return (
                    <>
                        <Form.Control 
                            className="input" 
                            as="textarea" 
                            rows={7}
                            maxLength="300"
                            required={field.required}
                            placeholder={field.placeholder}
                            onChange={(e) => handleFieldChange(field.label, e, 300)}  />
                        <p style={{ color: '#404042', float: 'right', fontSize: '12px'}}>
                            {getCharacterCount(field, fields)} / 300
                        </p>
                        {requiredError ? 
                        <Form.Control.Feedback type="invalid">
                            Required field empty.
                        </Form.Control.Feedback> : <></>}
                    </>
                );
            case "number":
                return (
                    <>
                        <Form.Control 
                            className="input" 
                            type="number" 
                            required={field.required}
                            placeholder={field.placeholder}
                            onChange={(e) => handleFieldChange(field.label, e, 0)} />
                        {requiredError ? 
                        <Form.Control.Feedback type="invalid">
                            Required field empty.
                        </Form.Control.Feedback> : <></>}
                    </>
                );
            case "dropdown":
                return (
                    <>
                        <Form.Select
                            className="input selectOption"
                            required={field.required}
                            onChange={(e) => handleFieldChange(field.label, e, 0)} >
                                <option value="" disabled selected>{field.placeholder ? field.placeholder : "Select"}</option>
                            {field.options.map((option,i) => 
                                <option key={i}>
                                    {option.name}
                                </option>)}
                        </Form.Select>
                        {requiredError ? 
                        <Form.Control.Feedback type="invalid">
                            Required field empty.
                        </Form.Control.Feedback> : <></>}
                    </>
                );
            case "date":
                return (
                    <>
                        <Form.Control 
                            className="input" 
                            required={field.required}
                            type="date" 
                            placeholder={field.placeholder}
                            onChange={(e) => handleFieldChange(field.label, e, 0)} />
                        {requiredError ? 
                        <Form.Control.Feedback type="invalid">
                            Required field empty.
                        </Form.Control.Feedback> : <></>}
                    </>
                );
            case "time":
                return (
                    <>
                        <Form.Control 
                            className="input" 
                            type="time" 
                            required={field.required}
                            placeholder={field.placeholder}
                            onChange={(e) => handleFieldChange(field.label, e, 0)} />
                        {requiredError ? 
                        <Form.Control.Feedback type="invalid">
                            Required field empty.
                        </Form.Control.Feedback> : <></>}
                    </>
                );
            case "attachment":
                return (<><br/>
                    <Form.Control 
                        type="file" 
                        id={field.label}
                        required={field.required}
                        onChange={(event) => handleDisplayImageDetails(field, event)} 
                        className="d-none" 
                        label={field.label}
                        multiple/>
                    {MAX_ATTACHMENTS - ((attachmentMap[`${field.label}`]?.uploadedFiles?.length) || 0) > 0 ?
                        <Button label={field.label} className="input" onClick={handleUpload} style={{ fontFamily: "Open Sans Medium", fontSize: "14px", color: "#e93864", width: "100%", textAlign: "left", marginBottom: "10px" }}>
                            + Add up to {MAX_ATTACHMENTS - ((attachmentMap[`${field.label}`]?.uploadedFiles?.length) || 0)} {MAX_ATTACHMENTS - ((attachmentMap[`${field.label}`]?.uploadedFiles?.length) || 0) > 1 ? "attachments" : "attachment"}
                        </Button> : ""}
                    {((attachmentMap[`${field.label}`]?.uploadedFiles?.length) || 0) > 0 ? 
                        Array.from(attachmentMap[`${field.label}`]?.uploadedFiles).map((file, index) => 
                        <div style={{ display: "flex", justifyContent: "space-between"}} key={index}>
                            <p className="grey font-bold" style={{ fontSize: "14px" }}>{file.name}</p> 
                            <span onClick={(event) => handleRemove(file, event, field)}><TrashIcon /></span>
                        </div>) : ""}
                    <div style={{ fontSize: "12px"}}>Allowed types: *.png, *.jpeg, *.pdf, *.doc, *.mp4</div>
                    <div className="font-bold" style={{ fontSize: "14px", color: "red"}}>{(attachmentMap[`${field.label}`]?.typeError) ? "Invalid file type." : ""}</div>
                    <div className="font-bold" style={{ fontSize: "14px", color: "red"}}>{(attachmentMap[`${field.label}`]?.lengthError) ? `You can only upload a maximum of ${MAX_ATTACHMENTS} files.` : ""}</div>
                    {requiredError ? 
                        <Form.Control.Feedback type="invalid">
                            Required field empty.
                        </Form.Control.Feedback> : <></>}
                </>);
            default:
                break;
        }
    }

    useEffect(() => {
        setFormBody(prevState => {
            return {
                ...prevState,
                articleId: props.article.id,
                articleName: props.article.name,
                tripId: trip?.id,
                receiptNo: trip?.receiptNo
            }
        })

        if (submitted && props.article.id !== DELETE_ACCOUNT_ARTICLE_ID) {
            navigate(`/issue/${issue.id}`, {state:{issue: issue}});
        } else if (submitted && props.article.id === DELETE_ACCOUNT_ARTICLE_ID) {
            window.location.href = 'ryde://open?action=back';
        }
    }, [submitted, issue])

    useEffect(() => {
        handleAttachmentMapInit()
    }, [])

    if (loading) return <Loading />
        
    return (
        <>  
            {trip ? 
            <>
                <h3 className="grey font-bold" style={{ fontSize: "14px", paddingTop: "20px"}}>
                    Your trip
                </h3>
                <TripCard trip={trip}/>
            </> 
            : <></>}
            <Form 
                noValidate 
                validated={validated}
                onSubmit={submitForm} >
                {formFields?.map((field,i) => 
                <Form.Group className="mb-3" key={i} >
                    <Form.Label className="grey font-bold" style={{ fontSize: "14px" }}>
                        {field.label} {field.required ? <span style={{ color: "red"}}>*</span> : <></>}
                    </Form.Label>
                    {showFields(field)}
                </Form.Group>
                )}
                <Button 
                    className="ryde-button"
                    style={{ fontSize: "14px"}}
                    type="submit">
                        {props.article.id === DELETE_ACCOUNT_ARTICLE_ID ? "Delete Account" : "Submit"}
                </Button>
                <Modal 
                    show={showModal} 
                    onHide={() => setShowModal(false)}
                    centered>
                    <Modal.Header closeButton>
                        <Modal.Title>Account Deletion Confirmation</Modal.Title>
                    </Modal.Header>
                    <Modal.Body className="pop-up">
                    Are you sure you want to delete your account? Please ensure you have read and understood 
                    the important reminders before proceeding. By proceeding, you accept Ryde’s {' '}
                    <Link to="https://www.rydesharing.com/terms-of-use/" target="_blank">Terms of Use</Link> & {' '}
                    <Link to="https://www.rydesharing.com/privacy-policy/" target="_blank">Privacy Policy.</Link>
                        <Button 
                            className="ryde-button" 
                            style={{ fontSize: "14px", marginTop: "15px" }}
                            onClick={() => { setShowModalConfirm(true); setShowModal(false); }}>
                        Agree & Confirm Delete Account
                    </Button>
                    </Modal.Body>
                </Modal>
                <Modal 
                    show={showModalConfirm} 
                    centered>
                    <Modal.Header>
                        <Modal.Title>We'll process your account deletion</Modal.Title>
                    </Modal.Header>
                    <Modal.Body className="pop-up">
                    Please note that the deletion process may take up to 2 weeks to complete.
                    If you change your mind within this period, please contact our support team immediately to cancel the deletion process.
                    Once the deletion is finalized, you will no longer have access to your account.
                    Thank you for being a part of our community.
                        <Button 
                            className="ryde-button" 
                            style={{ fontSize: "14px", marginTop: "15px" }}
                            onClick={(e) => { submitForm(e); setShowModalConfirm(false); }}>
                        Ok
                    </Button>
                    </Modal.Body>
                </Modal>
            </Form><br/><br/>
        </>
        );
  }

export default HelpFormInfo;